import type { CSSProperties } from 'react';

export type AutomobileModelImageFrame = {
  /** Yatay odak noktası (0–100, object-position) */
  focalX: number;
  /** Dikey odak noktası (0–100) */
  focalY: number;
  /** Ölçek (100 = normal; 40–250) */
  zoom: number;
};

export const DEFAULT_MODEL_IMAGE_FRAME: AutomobileModelImageFrame = {
  focalX: 50,
  focalY: 50,
  zoom: 100,
};

/** PNG uzaklaştırma — şeffaf kenarlar görünür */
export const MODEL_IMAGE_ZOOM_MIN = 40;
export const MODEL_IMAGE_ZOOM_MAX = 250;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

export function normalizeModelImageFrame(
  raw?: Partial<AutomobileModelImageFrame> | null,
): AutomobileModelImageFrame {
  if (!raw) return { ...DEFAULT_MODEL_IMAGE_FRAME };
  return {
    focalX: clamp(round(raw.focalX ?? DEFAULT_MODEL_IMAGE_FRAME.focalX), 0, 100),
    focalY: clamp(round(raw.focalY ?? DEFAULT_MODEL_IMAGE_FRAME.focalY), 0, 100),
    zoom: clamp(round(raw.zoom ?? DEFAULT_MODEL_IMAGE_FRAME.zoom), MODEL_IMAGE_ZOOM_MIN, MODEL_IMAGE_ZOOM_MAX),
  };
}

export function modelImageFrameFromRow(row?: {
  image_focal_x?: number | null;
  image_focal_y?: number | null;
  image_zoom?: number | null;
} | null): AutomobileModelImageFrame {
  return imageFrameFromDbFields(row, 'image_focal_x', 'image_focal_y', 'image_zoom');
}

export function logoFrameFromRow(row?: {
  logo_focal_x?: number | null;
  logo_focal_y?: number | null;
  logo_zoom?: number | null;
} | null): AutomobileModelImageFrame {
  return imageFrameFromDbFields(row, 'logo_focal_x', 'logo_focal_y', 'logo_zoom');
}

function imageFrameFromDbFields(
  row: Record<string, number | null | undefined> | null | undefined,
  xKey: string,
  yKey: string,
  zKey: string,
): AutomobileModelImageFrame {
  if (!row) return { ...DEFAULT_MODEL_IMAGE_FRAME };
  const hasAny = row[xKey] != null || row[yKey] != null || row[zKey] != null;
  if (!hasAny) return { ...DEFAULT_MODEL_IMAGE_FRAME };
  return normalizeModelImageFrame({
    focalX: row[xKey] ?? DEFAULT_MODEL_IMAGE_FRAME.focalX,
    focalY: row[yKey] ?? DEFAULT_MODEL_IMAGE_FRAME.focalY,
    zoom: row[zKey] ?? DEFAULT_MODEL_IMAGE_FRAME.zoom,
  });
}

export function isDefaultModelImageFrame(frame: AutomobileModelImageFrame): boolean {
  return (
    frame.focalX === DEFAULT_MODEL_IMAGE_FRAME.focalX &&
    frame.focalY === DEFAULT_MODEL_IMAGE_FRAME.focalY &&
    frame.zoom === DEFAULT_MODEL_IMAGE_FRAME.zoom
  );
}

export function modelImageFrameToPayload(frame: AutomobileModelImageFrame): {
  image_focal_x: number | null;
  image_focal_y: number | null;
  image_zoom: number | null;
} {
  const normalized = normalizeModelImageFrame(frame);
  if (isDefaultModelImageFrame(normalized)) {
    return { image_focal_x: null, image_focal_y: null, image_zoom: null };
  }
  return {
    image_focal_x: normalized.focalX,
    image_focal_y: normalized.focalY,
    image_zoom: normalized.zoom,
  };
}

export function logoFrameToPayload(frame: AutomobileModelImageFrame): {
  logo_focal_x: number | null;
  logo_focal_y: number | null;
  logo_zoom: number | null;
} {
  const normalized = normalizeModelImageFrame(frame);
  if (isDefaultModelImageFrame(normalized)) {
    return { logo_focal_x: null, logo_focal_y: null, logo_zoom: null };
  }
  return {
    logo_focal_x: normalized.focalX,
    logo_focal_y: normalized.focalY,
    logo_zoom: normalized.zoom,
  };
}

export function modelImageFrameStyle(frame: AutomobileModelImageFrame): CSSProperties {
  const { focalX, focalY, zoom } = normalizeModelImageFrame(frame);
  return {
    objectPosition: `${focalX}% ${focalY}%`,
    transform: zoom === 100 ? undefined : `scale(${zoom / 100})`,
    transformOrigin: `${focalX}% ${focalY}%`,
  };
}

export type ModelImageLayout = {
  imgStyle: CSSProperties;
  /** Tam görüntü sığdıktan sonra ekstra uzaklaştırmada şeffaf kenar */
  showTransparentLetterbox: boolean;
};

/**
 * Kadraj: %100 = cover (kırpılmış). Uzaklaştırınca önce kırpılan alanlar açılır (contain'e),
 * daha fazla uzaklaştırınca görüntü küçülür ve şeffaf kenarlar görünür.
 */
export function computeModelImageLayout(
  frame: AutomobileModelImageFrame,
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): ModelImageLayout {
  const absoluteBase: CSSProperties = {
    position: 'absolute',
    maxWidth: 'none',
    maxHeight: 'none',
    display: 'block',
  };

  if (containerWidth <= 0 || containerHeight <= 0 || imageWidth <= 0 || imageHeight <= 0) {
    return {
      imgStyle: absoluteBase,
      showTransparentLetterbox: frame.zoom < 100,
    };
  }

  const { focalX, focalY, zoom } = normalizeModelImageFrame(frame);
  const coverScale = Math.max(containerWidth / imageWidth, containerHeight / imageHeight);
  const containScale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
  const revealRatio = coverScale / containScale;
  const zoomContain = Math.max(MODEL_IMAGE_ZOOM_MIN, 100 / revealRatio);

  const coverLeft = (focalX / 100) * (containerWidth - imageWidth * coverScale);
  const coverTop = (focalY / 100) * (containerHeight - imageHeight * coverScale);
  const containLeft = (containerWidth - imageWidth * containScale) / 2;
  const containTop = (containerHeight - imageHeight * containScale) / 2;

  const layoutAt = (scale: number, left: number, top: number): ModelImageLayout => ({
    imgStyle: {
      ...absoluteBase,
      width: imageWidth * scale,
      height: imageHeight * scale,
      left,
      top,
    },
    showTransparentLetterbox: false,
  });

  if (zoom >= 100) {
    const scale = coverScale * (zoom / 100);
    return layoutAt(
      scale,
      (focalX / 100) * (containerWidth - imageWidth * scale),
      (focalY / 100) * (containerHeight - imageHeight * scale),
    );
  }

  if (zoom >= zoomContain) {
    const t = zoomContain >= 100 ? 1 : (100 - zoom) / (100 - zoomContain);
    const scale = coverScale + t * (containScale - coverScale);
    return layoutAt(
      scale,
      coverLeft + t * (containLeft - coverLeft),
      coverTop + t * (containTop - coverTop),
    );
  }

  const scale = containScale * (zoom / zoomContain);
  return {
    imgStyle: {
      ...absoluteBase,
      width: imageWidth * scale,
      height: imageHeight * scale,
      left: (containerWidth - imageWidth * scale) / 2,
      top: (containerHeight - imageHeight * scale) / 2,
    },
    showTransparentLetterbox: true,
  };
}

export function panModelImageFrame(
  frame: AutomobileModelImageFrame,
  deltaX: number,
  deltaY: number,
  boxWidth: number,
  boxHeight: number,
): AutomobileModelImageFrame {
  if (boxWidth <= 0 || boxHeight <= 0) return frame;
  const zoomFactor = Math.max(MODEL_IMAGE_ZOOM_MIN / 100, frame.zoom / 100);
  const next = normalizeModelImageFrame({
    focalX: frame.focalX - (deltaX / boxWidth) * 100 * zoomFactor,
    focalY: frame.focalY - (deltaY / boxHeight) * 100 * zoomFactor,
    zoom: frame.zoom,
  });
  return next;
}
